import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Alert, CircularProgress, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import LinkIcon from '@mui/icons-material/Link';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { BiltyPrint, printBilty } from '../../components/bilty/BiltyPrint';
import { biltyAPI, invoiceAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { RADIUS, brandCss } from '../../theme/appTheme';

export const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const { formatCurrency, formatDate } = useLocale();
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState('');
  const [invoice, setInvoice] = useState<Record<string, any> | null>(null);
  const [bilty, setBilty] = useState<Record<string, any> | null>(null);
  const [company, setCompany] = useState<Record<string, any> | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [biltyOptions, setBiltyOptions] = useState<Record<string, unknown>[]>([]);
  const [selectedBiltyId, setSelectedBiltyId] = useState('');

  const canPrint = hasPermission('invoices', 'print') || hasPermission('invoices', 'view');
  const canEdit = hasPermission('invoices', 'edit');
  const canCreateFromBilty = hasPermission('invoices', 'create');

  const printOptions = invoice ? {
    asPerInvoiceNo: invoice.invoiceNumber,
    invoiceDate: invoice.invoiceDate,
    invoiceValue: invoice.totalAmount,
    documentTitle: `Invoice ${invoice.invoiceNumber}`,
  } : undefined;

  const loadInvoice = () => {
    if (!id) return;
    setLoading(true);
    invoiceAPI.getById(id).then((res) => {
      const inv = res.data.invoice || res.data;
      setInvoice(inv);
      setCompany(res.data.company || null);
      setBilty(res.data.bilty || null);
      const biltyDoc = res.data.bilty;
      if (searchParams.get('print') === '1' && biltyDoc) {
        setTimeout(() => printBilty(biltyDoc, res.data.company, {
          formatCurrency,
          formatDate,
        }, {
          asPerInvoiceNo: inv.invoiceNumber,
          invoiceDate: inv.invoiceDate,
          invoiceValue: inv.totalAmount,
        }), 500);
      }
    }).catch(() => setError('Failed to load invoice')).finally(() => setLoading(false));
  };

  useEffect(() => { loadInvoice(); }, [id, searchParams, formatCurrency, formatDate]);

  const openLinkDialog = async () => {
    setLinkOpen(true);
    setSelectedBiltyId('');
    try {
      const res = await biltyAPI.getAll();
      const available = (res.data || []).filter((b: Record<string, unknown>) => !b.invoiceId);
      setBiltyOptions(available);
    } catch {
      setError('Failed to load bilties');
    }
  };

  const handleLinkBilty = async () => {
    if (!id || !selectedBiltyId) return;
    setLinking(true);
    setError('');
    try {
      await invoiceAPI.update(id, { bilty: selectedBiltyId });
      showToast('Bilty linked to invoice');
      setLinkOpen(false);
      loadInvoice();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; invoiceId?: string } } };
      setError(e.response?.data?.message || 'Failed to link bilty');
    } finally {
      setLinking(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (!invoice) {
    return <Alert severity="error">Invoice not found</Alert>;
  }

  if (!bilty) {
    return (
      <Box>
        <PageHeader
          title={`Invoice ${invoice.invoiceNumber}`}
          subtitle={getPageSubtitle('invoice_unlinked')}
          action={
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/invoices')}>Back</Button>
              {canEdit && (
                <Button variant="outlined" startIcon={<LinkIcon />} onClick={openLinkDialog}>
                  Link Bilty
                </Button>
              )}
              {canCreateFromBilty && (
                <Button
                  variant="contained"
                  startIcon={<ReceiptLongIcon />}
                  onClick={() => navigate('/bilty')}
                  sx={{ background: brandCss.gradientPrimary }}
                >
                  Create from Bilty
                </Button>
              )}
            </Box>
          }
        />

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Alert severity="warning" sx={{ mb: 2 }}>
          This invoice is not linked to a bilty. Open a bilty and use <strong>Create Invoice</strong>, or link an existing bilty below to print the transport LR format.
        </Alert>

        <Card sx={{ borderRadius: `${RADIUS}px`, mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 2 }}>
              <Box>
                <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>Date</Box>
                <Box sx={{ fontWeight: 700 }}>{formatDate(String(invoice.invoiceDate || ''))}</Box>
              </Box>
              <Box>
                <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>Subtotal</Box>
                <Box sx={{ fontWeight: 700 }}>{formatCurrency(Number(invoice.subtotal || 0))}</Box>
              </Box>
              <Box>
                <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>Total</Box>
                <Box sx={{ fontWeight: 700 }}>{formatCurrency(Number(invoice.totalAmount || 0))}</Box>
              </Box>
              <Box>
                <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>Payment</Box>
                <Box sx={{ fontWeight: 700, textTransform: 'capitalize' }}>{String(invoice.paymentStatus || 'pending')}</Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Link bilty to invoice</DialogTitle>
          <DialogContent>
            <TextField
              select
              fullWidth
              label="Select bilty"
              value={selectedBiltyId}
              onChange={(e) => setSelectedBiltyId(e.target.value)}
              sx={{ mt: 1 }}
            >
              {biltyOptions.length === 0 ? (
                <MenuItem value="" disabled>No unlinked bilties available</MenuItem>
              ) : (
                biltyOptions.map((b) => (
                  <MenuItem key={String(b._id)} value={String(b._id)}>
                    {String(b.biltyNumber)} — {String(b.source || '')} → {String(b.destination || '')}
                  </MenuItem>
                ))
              )}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!selectedBiltyId || linking}
              onClick={handleLinkBilty}
              sx={{ background: brandCss.gradientPrimary }}
            >
              Link & Load LR
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={`Invoice ${invoice.invoiceNumber}`}
        subtitle={`Linked bilty ${bilty.biltyNumber} · ${formatCurrency(Number(invoice.totalAmount || 0))}`}

        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/invoices')}>Back</Button>
            <Button variant="outlined" onClick={() => navigate(`/bilty/${bilty._id}`)}>View Bilty</Button>
            {canPrint && (
              <Button
                variant="contained"
                startIcon={<PrintOutlinedIcon />}
                onClick={() => printBilty(bilty, company, { formatCurrency, formatDate }, printOptions)}
                sx={{ background: brandCss.gradientPrimary }}
              >
                Print LR
              </Button>
            )}
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ borderRadius: `${RADIUS}px` }}>
        <CardContent>
          <BiltyPrint bilty={bilty} company={company} printOptions={printOptions} />
        </CardContent>
      </Card>
    </Box>
  );
};
